<template>
  <router-link
    :to="to"
    class="debate-card"
    :class="{ 'debate-card--live': isLive }"
    :aria-label="ariaLabel"
    style="position: relative"
  >
    <!-- Contextual options menu (only for actionable states when withMenu is true) -->
    <DebateOptionsMenu
      v-if="withMenu && state !== 'complete'"
      :debate-id="debateId"
      :debate-name="name"
      :state="state"
      placement="card"
      :show-open="state !== 'failed'"
      @open="onMenuOpen"
      @pause="emit('pause', $event)"
      @resume="emit('resume', $event)"
      @delete="emit('delete', $event)"
      @menu-open="menuIsOpen = true"
      @menu-close="menuIsOpen = false"
    />

    <!-- Eyebrow: short ID -->
    <p class="debate-card__eyebrow font-mono">
      {{ resolvedShortId }}
    </p>

    <!-- Name -->
    <h3 ref="nameRef" class="debate-card__name">
      {{ name }}
      <q-tooltip
        v-if="isTruncated"
        anchor="bottom middle"
        self="top middle"
        :delay="400"
        :aria-label="t('debates.list.nameTooltipAria')"
      >
        {{ name }}
      </q-tooltip>
    </h3>

    <!-- Protocol -->
    <p class="debate-card__protocol font-mono">{{ protocol }}</p>

    <!-- State row -->
    <div class="debate-card__state-row">
      <DebateStateBadge :state="state" />
      <span class="debate-card__turns font-mono">
        {{ t('debates.list.turnsCount', { consumed: consumedTurns, total: totalTurns }) }}
      </span>
    </div>

    <!-- Footer: relative time + optional verdict link -->
    <footer class="debate-card__footer">
      <RelativeTime :timestamp="updatedAt" />
      <a
        v-if="verdictTo && state === 'complete'"
        class="debate-card__verdict-link"
        :href="resolvedVerdictHref"
        @click.prevent.stop="navigateToVerdict"
      >
        <svg class="debate-card__verdict-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M5 12h14M12 5l7 7-7 7" />
        </svg>
        {{ t('debates.list.viewVerdict') }}
      </a>
    </footer>
  </router-link>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import type { RouteLocationRaw } from 'vue-router';
import DebateStateBadge from '@/components/DebateStateBadge.vue';
import DebateOptionsMenu from '@/components/DebateOptionsMenu.vue';
import RelativeTime from '@/components/RelativeTime.vue';
import type { DebateState } from '@/types/debate';

const props = defineProps<{
  shortId: string;
  name: string;
  protocol: string;
  state: DebateState;
  consumedTurns: number;
  totalTurns: number;
  updatedAt: string;
  to: RouteLocationRaw;
  /** Optional route to the verdict page. When provided and state=complete, shows a "View verdict" link. */
  verdictTo?: RouteLocationRaw;
  /** When true, renders the DebateOptionsMenu trigger on hover. */
  withMenu?: boolean;
  /** The debate ID, required when withMenu=true to wire actions. */
  debateId?: string;
}>();

const emit = defineEmits<{
  pause: [id: string];
  resume: [id: string];
  delete: [id: string];
  open: [id: string];
}>();

const { t } = useI18n();
const router = useRouter();

// ---------------------------------------------------------------------------
// State local
// ---------------------------------------------------------------------------

/** Tracks whether the options menu is open to keep the trigger visible. */
const menuIsOpen = ref(false);

// ---------------------------------------------------------------------------
// Computed
// ---------------------------------------------------------------------------

const isLive = computed(() => props.state === 'seeding' || props.state === 'running');

const resolvedShortId = computed(() =>
  props.shortId || t('debates.shortIdFallback')
);

const ariaLabel = computed(() =>
  t('debates.list.cardAria', {
    name: props.name,
    state: props.state,
    consumed: props.consumedTurns,
    total: props.totalTurns,
    relativeTime: props.updatedAt
  })
);

/** Resolves the verdictTo route to an absolute href for the <a> tag. */
const resolvedVerdictHref = computed<string>(() => {
  if (!props.verdictTo) return '#';
  try {
    return router.resolve(props.verdictTo).href;
  } catch {
    return '#';
  }
});

// ---------------------------------------------------------------------------
// Tooltip — detect overflow on the name element
// ---------------------------------------------------------------------------

const nameRef = ref<HTMLElement | null>(null);
const isTruncated = ref(false);

/** Checks if the name element is truncated (scrollHeight > clientHeight). */
const checkTruncation = (): void => {
  const el = nameRef.value;
  if (!el) return;
  isTruncated.value = el.scrollHeight > el.clientHeight;
};

let resizeObserver: ResizeObserver | null = null;

// ---------------------------------------------------------------------------
// Functions
// ---------------------------------------------------------------------------

/** Navigates to the verdict page without triggering the parent router-link. */
const navigateToVerdict = (): void => {
  if (!props.verdictTo) return;
  void router.push(props.verdictTo);
};

/** Opens the debate room page from the options menu. */
const onMenuOpen = (debateId: string): void => {
  void router.push(props.to);
  emit('open', debateId);
};

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

onMounted(() => {
  // Wait for fonts to be ready for accurate measurement (D4)
  void nextTick(() => {
    void (document.fonts?.ready ?? Promise.resolve()).then(() => {
      checkTruncation();
    });
  });

  if (typeof ResizeObserver !== 'undefined' && nameRef.value) {
    resizeObserver = new ResizeObserver(() => {
      checkTruncation();
    });
    resizeObserver.observe(nameRef.value);
  }
});

onUnmounted(() => {
  resizeObserver?.disconnect();
});
</script>

<style scoped lang="scss">
.debate-card {
  // Variables
  --card-bg: var(--paper);

  // Layout
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 260px;
  max-width: 340px;
  padding: var(--space-5);

  // Box model
  border-radius: var(--r-4);
  border: var(--border);

  // Visuals
  background: var(--card-bg);
  box-shadow: var(--shadow-1);

  // Typography
  text-decoration: none;
  color: var(--ink);

  // Transition
  transition:
    box-shadow var(--d-fast) var(--ease-out),
    border-color var(--d-fast) var(--ease-out),
    background-color var(--d-fast) var(--ease-out);

  &--live {
    box-shadow: var(--shadow-2);

    &:hover {
      box-shadow: var(--shadow-3);
      transform: translateY(-1px);
    }
  }

  &:hover {
    box-shadow: var(--shadow-2);
    border-color: var(--mist-2);

    :deep(.debate-options-menu) {
      opacity: 1;
    }
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  &__eyebrow {
    margin: 0;
    font-size: 10.5px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--stone);
  }

  &__name {
    margin: 0;
    font-size: var(--fs-body);
    font-weight: var(--fw-semibold);
    line-height: 1.25;
    color: var(--ink);

    // Truncate 2 lines
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__protocol {
    margin: 0;
    font-size: var(--fs-mono);
    color: var(--stone);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__state-row {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  &__turns {
    font-size: var(--fs-mono);
    color: var(--stone);
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
    margin-top: auto;
    padding-top: var(--space-2);
    border-top: var(--border);
  }

  &__verdict-link {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    font-size: var(--fs-body-sm);
    color: var(--stone);
    text-decoration: none;
    transition:
      color var(--d-fast) var(--ease-out),
      transform var(--d-fast) var(--ease-out);
    white-space: nowrap;
    flex-shrink: 0;

    &:hover {
      color: var(--ink);
      transform: translateX(2px);
    }

    &:focus-visible {
      outline: none;
      box-shadow: var(--shadow-focus);
      border-radius: var(--r-1);
    }
  }

  &__verdict-icon {
    flex-shrink: 0;
  }
}

@media (hover: none) {
  .debate-card :deep(.debate-options-menu) {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .debate-card {
    transition: none;
  }
}

@media (max-width: 599px) {
  .debate-card {
    min-width: min(260px, 90vw);
    max-width: min(260px, 90vw);
  }
}

@media (min-width: 600px) and (max-width: 1024px) {
  .debate-card {
    min-width: 260px;
    max-width: 300px;
  }
}
</style>
